import { makeAPICall } from '@gokwik/utilities'
import APIEndPoints from '@library/utilities/constants/apiEndpoints'
import { handleError } from '@library/utilities/helpers/handleError'
import dayjs from 'dayjs'
import { OFFER_CATEGORY } from '../constants'
import { BankRow, EmiCode } from './BankCodesInput'

export enum MODE {
    CREATE = 'create',
    DRAFT_EDIT = 'draft-edit',
    EDIT = 'edit',
}

let dropdownData = {
    [OFFER_CATEGORY.CASHBACK]: null,
    [OFFER_CATEGORY.PAYMENT_PARTNER]: null,
    [OFFER_CATEGORY.NO_COST_EMI]: null,
}

const createPayloadFields = [
    'offer_category',
    'coupon_code',
    'title',
    'payment_offer_title',
    'payment_offer_sub_title',
    'description',
    'tnc',
    'view_in_listing',
    'merchant_id',
    'start_date',
    'end_date',
    'is_global_offer',
    'override_badge_text',
    'logo_url',
]

const updatePayloadFields = [
    'coupon_code',
    'title',
    'payment_offer_title',
    'payment_offer_sub_title',
    'description',
    'tnc',
    'view_in_listing',
    'start_date',
    'end_date',
    'is_global_offer',
    'override_badge_text',
    'valid_payment_methods',
    'auto_apply',
    'max_coupon_count',
    'coupon_per_customer',
    'merchant_ids',
    'discount_type',
    'payment_method_type',
    'rule_detail',
    'logo_url',
    'valid_banks',
    'valid_bank_codes',
]

const updatePublishedPayloadFields = [
    'title',
    'payment_offer_title',
    'payment_offer_sub_title',
    'description',
    'tnc',
    'view_in_listing',
    'end_date',
    // 'is_global_offer',
    // 'override_badge_text',
    // 'auto_apply',
    'merchant_ids',
    'logo_url',
]

export const getOfferDropDownData = async (offerCategory: OFFER_CATEGORY, callback: CallableFunction) => {
    if (dropdownData[offerCategory]) {
        callback(dropdownData[offerCategory])
        return
    }
    try {
        let response = await makeAPICall({
            method: 'get',
            url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.offers.dropdownData,
            params: { offerCategory },
        })
        if (response?.success && response?.data?.data) {
            dropdownData[offerCategory] = response.data.data
            callback(response.data.data)
        } else {
            callback(null)
            handleError(response)
        }
    } catch (error) {
        callback(null)
        handleError(error)
    }
}

export const resetDropDownData = () => {
    dropdownData = {
        [OFFER_CATEGORY.CASHBACK]: null,
        [OFFER_CATEGORY.PAYMENT_PARTNER]: null,
        [OFFER_CATEGORY.NO_COST_EMI]: null,
    }
}

export const getEmiCodes = async (bankCode: string, callback: CallableFunction) => {
    if (dropdownData[OFFER_CATEGORY.NO_COST_EMI]?.[bankCode]) {
        callback(dropdownData[OFFER_CATEGORY.NO_COST_EMI][bankCode])
        return
    }
    try {
        let response = await makeAPICall({
            method: 'get',
            url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.offers.emiCodes,
            params: { bankCode },
        })
        if (response?.success && response?.data?.data) {
            if (!dropdownData[OFFER_CATEGORY.NO_COST_EMI]) dropdownData[OFFER_CATEGORY.NO_COST_EMI] = {}
            dropdownData[OFFER_CATEGORY.NO_COST_EMI][bankCode] = response.data.data
            callback(response.data.data)
        } else {
            callback(null)
            handleError(response)
        }
    } catch (error) {
        callback(null)
        handleError(error)
    }
}

export const getDraftOffer = async (id, callback?: CallableFunction) => {
    try {
        let response = await makeAPICall({
            method: 'get',
            url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.offers.draftOffer(id),
        })
        if (response?.success && response?.data?.data) {
            callback?.(response.data.data)
        } else {
            callback?.(null)
            handleError(response)
        }
    } catch (error) {
        callback?.(null)
        handleError(error)
    }
}

export const deleteDraftOffer = async (id, callback?: CallableFunction) => {
    try {
        let response = await makeAPICall({
            method: 'delete',
            url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.offers.draftOffer(id),
        })
        if (response?.success) {
            callback?.(true)
        } else {
            callback?.(null)
            handleError(response)
        }
    } catch (error) {
        callback?.(null)
        handleError(error)
    }
}

export const createDraftOffer = async (payload, callback?: CallableFunction) => {
    try {
        let response = await makeAPICall({
            method: 'post',
            url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.offers.createDraftOffer,
            payload,
        })
        if (response?.success && response?.data?.data) {
            callback?.(response.data.data || true)
        } else {
            callback?.(null)
            handleError(response)
        }
    } catch (error) {
        callback?.(null)
        handleError(error)
    }
}

export const updateDraftOffer = async (payload, id, callback?: CallableFunction) => {
    try {
        let response = await makeAPICall({
            method: 'patch',
            url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.offers.draftOffer(id),
            payload,
        })
        if (response?.success) {
            callback?.(true)
        } else {
            callback?.(null)
            handleError(response)
        }
    } catch (error) {
        callback?.(null)
        handleError(error)
    }
}

export const publishDraftOffer = async (id, callback?: CallableFunction) => {
    try {
        let response = await makeAPICall({
            method: 'patch',
            url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.offers.publishDraftOffer(id),
        })
        if (response?.success) {
            callback?.(true)
        } else {
            callback?.(null)
            handleError(response)
        }
    } catch (error) {
        callback?.(null)
        handleError(error)
    }
}

export const transformValuesToPayload = (values, merchant_id, mode: MODE) => {
    let payload: any = {
        offer_category: values.offer_category,
        coupon_code: values.coupon_code?.toUpperCase(),
        title: values.title,
        payment_offer_title: values.payment_offer_title,
        payment_offer_sub_title: values.payment_offer_sub_title,
        description: values.description,
        logo_url: values.logo_url,
        view_in_listing: values.offer_category === OFFER_CATEGORY.NO_COST_EMI ? false : values.view_in_listing ?? true,
        merchant_id,
        start_date: values.period?.[0]?.format(),
        end_date: values.period?.[1]?.format(),
        tnc: values.tnc?.replace?.(/(?:\r\n|\r|\n)/g, '||'),
        is_global_offer: values.is_global_offer ?? false,
        override_badge_text: values.override_badge_text ?? false,
        discount_type: values.discount_type,
        max_coupon_count: isNaN(values.max_coupon_count) ? undefined : +values.max_coupon_count,
        coupon_per_customer: isNaN(values.coupon_per_customer) ? undefined : Math.max(+values.coupon_per_customer, 1),
        payment_method_type: values.payment_method_type,
        auto_apply: [OFFER_CATEGORY.CASHBACK, OFFER_CATEGORY.NO_COST_EMI].includes(values.offer_category)
            ? true
            : values.auto_apply,
        rule_detail: getRuleDetails(values),
        valid_payment_methods: getValidPaymentMethods(values)?.map((method) => method?.replace(/[\"]/g, '')),
        merchant_ids: values.merchants
            ? Object.entries(values.merchants)
                  .filter((entry) => entry[1])
                  .map((entry) => entry[0])
            : undefined,
        valid_banks: getValidBanks(values),
        ...getValidBankCodes(values),
    }
    let allowedFields
    switch (mode) {
        case MODE.DRAFT_EDIT:
            allowedFields = updatePayloadFields
            break

        case MODE.EDIT:
            allowedFields = updatePublishedPayloadFields
            break

        case MODE.CREATE:
        default:
            allowedFields = createPayloadFields
            break
    }
    payload = Object.fromEntries(
        Object.entries(payload).filter((entry) => allowedFields.includes(entry[0]) && entry[1] !== null),
    )
    return payload
}

function getRuleDetails(values) {
    const ruleObj = {
        conditions: [],
        actions: [],
    }

    ruleObj.conditions.push({
        key: 'cart_value',
        operator: '>=',
        value: (+values.min_cart || 0).toString(),
    })

    if (values.max_cart) {
        ruleObj.conditions.push({
            key: 'cart_value',
            operator: '<=',
            value: values.max_cart?.toString(),
        })
    }

    if (['cards', 'emi'].includes(values.payment_method_type)) {
        ruleObj.conditions.push({
            key: 'bin',
            operator: 'in',
            value: values.bins ?? [],
        })
    } else if (values.payment_method_type === 'wallet') {
        ruleObj.conditions.push({
            key: 'wallet',
            operator: 'in',
            value: [`"${values.wallets?.replace(/[\"]/g, '')}"`],
        })
        ruleObj.actions.push({
            key: 'allowed_instruments',
            value: `new String[]{"${values.wallets?.replace(/[\"]/g, '')}"}`,
        })
    } else if (values.payment_method_type === 'upi') {
        ruleObj.conditions.push({
            key: 'psp_app',
            operator: 'in',
            value: [`"${values.psp_apps?.replace(/[\"]/g, '')}"`],
        })
        ruleObj.actions.push({
            key: 'allowed_instruments',
            value: `new String[]{"${values.psp_apps?.replace(/[\"]/g, '')}"}`,
        })
    }

    if (values.discount_type === 'fixed' && !isNaN(values.amount)) {
        ruleObj.actions.push({
            key: values.offer_category === OFFER_CATEGORY.CASHBACK ? 'cashback_amount' : 'discount_amount',
            value: values.amount?.toString(),
        })
    } else if (values.discount_type === 'percentage' && !isNaN(values.percentage)) {
        ruleObj.actions.push(
            ...[
                {
                    key:
                        values.offer_category === OFFER_CATEGORY.CASHBACK
                            ? 'cashback_percentage'
                            : 'discount_percentage',
                    value: values.percentage?.toString(),
                },
                values.max_discount_amount
                    ? {
                          key:
                              values.offer_category === OFFER_CATEGORY.CASHBACK
                                  ? 'max_cashback_amount'
                                  : 'max_discount_amount',
                          value: values.max_discount_amount?.toString(),
                      }
                    : null,
            ].filter(Boolean),
        )
    }

    if (values.override_badge_text) {
        ruleObj.actions.push({
            key: 'offer_title',
            value: `"${values.offer_title?.replace(/[\"]/g, '')}"`,
        })
    }

    return ruleObj
}

function getValidPaymentMethods(values) {
    switch (values.payment_method_type) {
        case 'cards':
            return values.cards ?? []

        case 'emi':
            return values.emis ?? []

        case 'wallet':
            return [values.wallets]

        case 'upi':
            return ['upi']

        default:
            return []
    }
}

function getValidBanks(values) {
    if (values.payment_method_type === 'emi' && values.show_valid_banks) {
        if (values.show_valid_banks === 'all') {
            return dropdownData[OFFER_CATEGORY.PAYMENT_PARTNER]?.banks?.map((bank) => bank.value)
        }
        if (values.show_valid_banks === 'specific') {
            return values.valid_banks
        }
    }
    return undefined
}

function mapCardToEmiMethod(cards: string[]) {
    return cards.map((card) => {
        switch (card) {
            case 'credit_card_emi':
                return 'emi-cc'

            case 'debit_card_emi':
                return 'emi-dc'

            default:
                return card
        }
    })
}

function getValidBankCodes(values) {
    if (values.offer_category === OFFER_CATEGORY.NO_COST_EMI && values.valid_bank_codes?.length) {
        let methods: Set<string> = new Set()
        const valid_bank_codes = values.valid_bank_codes.reduce((prev, bank: BankRow) => {
            bank.methods.forEach((method) => methods.add(method))
            const codes = Object.values(bank.tenures).reduce((p: string[], c: string[]) => [...p, ...c], [])
            return [...prev, ...codes]
        }, [])

        return {
            valid_bank_codes,
            valid_payment_methods: mapCardToEmiMethod(Array.from(methods)),
            payment_method_type: 'emi',
            discount_type: 'fixed',
        }
    }
    return {}
}

function getDataFromRuleDetails(data) {
    const details = {
        offer_title: undefined,
        amount: undefined,
        percentage: undefined,
        max_discount_amount: undefined,
        limit_discount_amount: false,
        min_cart: 0,
        max_cart: undefined,
        cards: undefined,
        emis: undefined,
        bins: undefined,
        wallets: undefined,
        psp_apps: undefined,
    }

    if (!data) return details

    data?.rule_detail?.conditions?.forEach((condition) => {
        if (condition.key === 'cart_value') {
            if (condition.operator === '>=') {
                details.min_cart = +condition.value
            }
            if (condition.operator === '<=') {
                details.max_cart = +condition.value
            }
        }
        if (condition.key === 'bin') {
            details.bins = condition.value
        }
        if (condition.key === 'wallet') {
            details.wallets = condition.value?.map((wallet) => wallet?.replace(/[\"]/g, ''))?.[0]
        }
        if (condition.key === 'psp_app') {
            details.psp_apps = condition.value?.map((app) => app?.replace(/[\"]/g, ''))?.[0]
        }
    })

    data?.rule_detail?.actions?.forEach((action) => {
        if (['cashback_percentage', 'discount_percentage'].includes(action.key)) {
            details.percentage = +action.value
        }
        if (['cashback_amount', 'discount_amount'].includes(action.key)) {
            details.amount = +action.value
        }
        if (['max_cashback_amount', 'max_discount_amount'].includes(action.key)) {
            details.max_discount_amount = +action.value
            details.limit_discount_amount = true
        }
        if (action.key === 'offer_title') {
            details.offer_title = action.value?.replace(/[\"]/g, '')
        }
    })

    if (data?.payment_method_type === 'cards') {
        details.cards = data.valid_payment_methods?.map((card) => card?.replace(/[\"]/g, ''))
    }

    if (data?.payment_method_type === 'emi') {
        details.emis = data.valid_payment_methods?.map((emi) => emi?.replace(/[\"]/g, ''))
    }

    return details
}

function getMerchants(data, mode: MODE) {
    switch (mode) {
        case MODE.DRAFT_EDIT:
            return data.merchant_ids?.reduce((prev, mid) => ({ ...prev, [mid]: true }), {}) || {}

        case MODE.EDIT:
            return data.merchant_offer_configs?.reduce((merchants, curr) => {
                merchants[curr.merchant_id] = !curr.is_deleted
                return merchants
            }, {})

        default:
            return {}
    }
}

async function prepareValidBankCodes(values) {
    if (values.offer_category === OFFER_CATEGORY.NO_COST_EMI) {
        await getOfferDropDownData(OFFER_CATEGORY.NO_COST_EMI, () => null)
        if (!values.valid_bank_codes?.length) return []

        const banks: BankRow[] = []
        for (let i = 0; i < values.valid_bank_codes.length; i++) {
            const originalCode: string = values.valid_bank_codes[i]
            if (!originalCode.toLowerCase().startsWith('gk_')) continue

            const bankCode = originalCode.match(/GK\_EMI\_([a-zA-Z]+)_/)?.[1]
            if (!bankCode) continue

            const codes = await new Promise<EmiCode[]>((resolve) => {
                getEmiCodes(bankCode, (data) => resolve(data || []))
            })

            const code = codes.find((emi) => emi.gk_code === originalCode)
            if (!code) continue

            const bankRef = banks.find((bank) => bank.bank === bankCode)
            if (bankRef) {
                bankRef.methods = Array.from(new Set([...(bankRef.methods ?? []), code.method]))
                if (!bankRef.tenures) bankRef.tenures = {}
                bankRef.tenures[code.method] = Array.from(
                    new Set([...(bankRef.tenures[code.method] ?? []), originalCode]),
                )
            } else {
                banks.push({
                    bank: bankCode,
                    methods: [code.method],
                    tenures: {
                        [code.method]: [originalCode],
                    },
                })
            }
        }
        return banks
    }
    return undefined
}

export const reverseTransformOffer = async (data, mode: MODE = MODE.DRAFT_EDIT) => {
    let ruleDetails = {}
    if (mode === MODE.DRAFT_EDIT) ruleDetails = getDataFromRuleDetails(data)
    if (mode === MODE.EDIT) ruleDetails = data.parsedRuleObj

    return {
        offer_category: data.offer_category,
        coupon_code: data.coupon_code,
        title: data.title,
        payment_offer_title: data.payment_offer_title,
        payment_offer_sub_title: data.payment_offer_sub_title,
        description: data.description,
        tnc: data.tnc?.replace?.(/(?:\|\|)/g, '\n'),
        override_badge_text: data.override_badge_text,
        logo_url: data.logo_url,
        view_in_listing: data.view_in_listing ?? true,
        period: [dayjs(data.start_date), dayjs(data.end_date)],
        discount_type: data.discount_type,
        max_coupon_count: +data.max_coupon_count,
        coupon_per_customer: +data.coupon_per_customer,
        payment_method_type: data.payment_method_type,
        show_valid_banks: 'specific',
        valid_banks: data.valid_banks,
        valid_bank_codes: await prepareValidBankCodes(data),
        auto_apply: data.auto_apply,
        is_global_offer: data.is_global_offer ?? false,
        merchants: getMerchants(data, mode),
        ...ruleDetails,
    }
}

export const addRemoveMerchants = (payload, initialMerchants = {}) => {
    const initialAddedMerchants = Object.fromEntries(Object.entries(initialMerchants).filter((entry) => entry[1]))

    const merchantIdsToAdd = payload.merchant_ids?.filter((id) => !initialAddedMerchants[id]) || []
    const merchantIdsToRemove =
        Object.keys(initialAddedMerchants).filter((id) => !payload.merchant_ids?.includes(id)) || []

    if (merchantIdsToAdd?.length) {
        payload.merchantIdsToAdd = merchantIdsToAdd
    }

    if (merchantIdsToRemove?.length) {
        payload.merchantIdsToRemove = merchantIdsToRemove
    }

    delete payload.merchant_ids

    return payload
}

export const getCardLabel = (card) => {
    switch (card) {
        case 'credit_card_emi':
            return 'Credit Card'

        case 'debit_card_emi':
            return 'Debit Card'

        case 'cardless_emi':
            return 'Cardless'

        default:
            return ''
    }
}
